import React, { useCallback, useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tooltip } from 'primereact/tooltip';
import { LINE_ITEMS_COLUMNS } from './LineItemsTable.config';
import styles from './LineItemsTable.styles.scss';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import { FormikField } from '../../../../../../components/FormikField';
import { lineItemsInputs } from '../../formConfig/inputs';
import { useCustomContext } from '../../../../../../context/customContext';
import {
  getComponentProps,
  getInputType,
  handleAddItemClick,
} from '../../helpers';
import {
  InputField,
  SelectField,
} from '../../../../../../components/FormikFields';
import {
  formatNumberWithCurrency,
  getProductWithTotals,
  getVatRateLabelFromValue,
  getLocalizedCurrencyLabel,
  getTaxTypes,
} from '../../../../../../helpers/invoiceHelpers';

const LineItemsTable = ({
  values,
  onDeleteItemClick,
  setFieldValue,
  setFieldTouched,
  taxCountry,
  validateForm,
  setTouched,
  setErrors,
  setValues,
  currency,
}) => {
  const { t, i18n } = useTranslation();
  const { countryData } = useCustomContext();
  const [originalData, setOriginalData] = useState({});
  const [localValues, setLocalValues] = useState(values);

  // Sync localValues with values prop when it changes (e.g., when loading a draft)
  useEffect(() => {
    setLocalValues(values);
  }, [values]);

  const fields = lineItemsInputs({
    values,
    setFieldValue,
    setFieldTouched,
    taxCountry,
    language: i18n.language,
    countryData,
  });

  const handleColumnValueChange = (e, options, col) => {
    const newVal = localValues.items.map((item, index) => {
      if (index === options.rowIndex) {
        const updatedItem = {
          ...item,
          [col.field]: e,
          ...(col.resetField ? { [col.resetField]: '' } : {}),
        };

        // If vatRateType is changed, update vatRate accordingly
        if (col.field === 'vatRateType') {
          const taxTypes = getTaxTypes(taxCountry, i18n.language);
          const selectedTaxType = taxTypes.find((type) => type.value === e);
          if (selectedTaxType) {
            updatedItem.vatRate = selectedTaxType.rate + '%';
          }
        }

        // Recalculate net amount, vat amount, and total amount on the fly
        const productWithTotals = getProductWithTotals({
          item: updatedItem,
          taxCountry,
        });

        return productWithTotals;
      }
      return item;
    });
    setLocalValues({ ...localValues, items: newVal });
  };

  const columnEditor = useCallback(
    (options, col) => {
      const field = fields.find((innerField) => innerField.id === col.field);

      if (!col.type) {
        return localValues.items[options.rowIndex][col.field];
      }

      if (col.type === 'text') {
        return (
          <InputField
            disabled={col.disabled?.(localValues.items[options.rowIndex])}
            onChange={(e) =>
              handleColumnValueChange(e.target.value, options, col)
            }
            value={localValues.items[options.rowIndex][col.field]}
            inputMode={field?.inputMode}
            pattern={field?.pattern}
            onKeyPress={field?.onKeyPress}
          />
        );
      } else if (col.type === 'select') {
        return (
          <SelectField
            field={{ value: localValues.items[options.rowIndex][col.field] }}
            value={localValues.items[options.rowIndex][col.field]}
            options={field?.options || []}
            onChange={(e) => {
              handleColumnValueChange(e, options, col);
            }}
          />
        );
      }
    },
    [localValues, fields],
  );

  const lineColumnsBody = useCallback(
    (data, item, col) => {
      const field = fields.find((innerField) => innerField.id === col.field);
      if (data === 'NEW ROW' && field) {
        // For display-only fields, just show the value as text
        if (field.type === 'display') {
          return (
            <div style={{ padding: '0.5rem' }}>
              {values.tempItem[field.id]
                ? formatNumberWithCurrency(
                    values.tempItem[field.id],
                    currency,
                    i18n.language,
                  )
                : formatNumberWithCurrency('0.000', currency, i18n.language)}
            </div>
          );
        }
        return (
          <FormikField
            name={field.name}
            component={getInputType(field?.type)}
            controlProps={{
              labelProps: { className: styles.label },
              className: field?.className,
            }}
            tooltip={field?.tooltip}
            componentProps={{
              ...getComponentProps({ ...field, t }),
            }}
          />
        );
      } else {
        // Display label for vatRateType instead of the value
        if (col.field === 'vatRateType') {
          const value = data[field?.id] || data[item.field];
          return getVatRateLabelFromValue(value, i18n.language, taxCountry);
        }
        return col.body
          ? col.body({ data, field, currency })
          : data[field?.id] || data[item.field];
      }
    },
    [fields, values.tempItem, i18n.language, taxCountry],
  );

  const rowEditorTemplate = useCallback(
    (rowData, options) => {
      if (rowData !== 'NEW ROW') {
        return (
          <div key={options.rowIndex} className="d-flex align-items-center">
            {options?.rowEditor?.editing ? (
              <>
                <Button
                  icon="pi pi-check"
                  type="button"
                  rounded
                  text
                  onClick={(e) => {
                    options.rowEditor.onSaveClick(e);
                    const productWithTotals = getProductWithTotals({
                      item: localValues.items[options.rowIndex],
                      taxCountry,
                    });
                    setFieldValue(
                      'items',
                      localValues.items.map((item, index) =>
                        index === options.rowIndex ? productWithTotals : item,
                      ),
                    );
                  }}
                />
                <Button
                  icon="pi pi-times"
                  type="button"
                  rounded
                  text
                  onClick={(e) => {
                    options.rowEditor.onCancelClick(e);
                    const updatedItems = values.items.map((item) =>
                      item.id === rowData.id ? originalData[rowData.id] : item,
                    );
                    setFieldValue('items', [...updatedItems]);
                    setLocalValues({ ...values, items: [...updatedItems] });
                  }}
                />
              </>
            ) : (
              <>
                <Button
                  icon="pi pi-file-edit"
                  type="button"
                  rounded
                  text
                  onClick={(e) => {
                    options.rowEditor.onInitClick(e);
                    setOriginalData((prev) => ({
                      ...prev,
                      [rowData.id]: { ...rowData },
                    }));
                  }}
                />
                <Button
                  icon="pi pi-trash"
                  type="button"
                  rounded
                  text
                  onClick={() => {
                    onDeleteItemClick(options.rowIndex, {
                      values,
                      setFieldValue,
                    });
                  }}
                />
              </>
            )}
          </div>
        );
      }
    },
    [values.items, originalData, localValues],
  );

  const footer = useCallback(
    () => (
      <Button
        text
        type="button"
        label={t('createInvoice.add-new-line')}
        icon="pi pi-plus-circle"
        className="color-theme-primary"
        onClick={async () => {
          setFieldValue('isCreatingItem', true);
          const formValues = await handleAddItemClick({
            values,
            validateForm,
            setTouched,
            setErrors,
            setValues,
            t,
          });
          setLocalValues(formValues);
        }}
      />
    ),
    [values],
  );

  const columnBody = useCallback((_data, options) => options.rowIndex + 1, []);

  return (
    <div className={cn('line-items-table', styles.container)}>
      <DataTable
        scrollable
        scrollHeight="400px"
        editMode="row"
        footer={footer}
        value={[...values.items, 'NEW ROW']}
        dataKey="id"
        tableStyle={{ minWidth: '50rem' }}
        className="line-items-datatable"
      >
        <Column header="#" headerStyle={{ width: '3rem' }} body={columnBody} />
        {LINE_ITEMS_COLUMNS.map((col) => {
          const showCurrency = [
            'discountAmount',
            'netAmount',
            'vatAmount',
            'totalAmount',
          ].includes(col.field);
          const headerText =
            showCurrency && currency
              ? `${t(col.label)} (${getLocalizedCurrencyLabel(
                  currency,
                  i18n.language,
                )})`
              : t(col.label);

          return (
            <Column
              key={col.key}
              field={col.key}
              headerStyle={col.headerStyle}
              header={
                <>
                  {headerText}
                  {col.required && <span style={{ color: 'red' }}> *</span>}
                  {col.hasInfoIcon && (
                    <>
                      <i
                        className={`pi pi-info-circle info-icon-${col.key}`}
                        style={{
                          marginLeft: '4px',
                          fontSize: '0.9em',
                        }}
                        data-pr-tooltip={t(col.tooltip)}
                      />
                      <Tooltip target={`.info-icon-${col.key}`} />
                    </>
                  )}
                </>
              }
              minWidth={'50rem'}
              editor={(options) => columnEditor(options, col)}
              body={(data, item) => lineColumnsBody(data, item, col)}
            />
          );
        })}
        <Column
          rowEditor
          field="action"
          header={t('common.actions')}
          body={rowEditorTemplate}
        />
      </DataTable>
    </div>
  );
};

export default React.memo(LineItemsTable);
